import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, isSameDay, parseISO, isToday } from 'date-fns';
import { getCalendarEvents, createCalendarEvent, acceptCalendarEvent, tentativeCalendarEvent, declineCalendarEvent } from '../api';

const SHOW_AS_COLORS = {
  busy: '#0078d4',
  tentative: '#f7a800',
  free: '#107c10',
  oof: '#c50f1f',
  workingElsewhere: '#8764b8',
};

function EventDetail({ event, onClose, onResponse }) {
  const [responding, setResponding] = useState(false);
  const [response, setResponse] = useState(event.responseStatus?.response);

  const start = parseISO(event.start.dateTime || event.start.date);
  const end = parseISO(event.end.dateTime || event.end.date);
  const isAllDay = event.isAllDay;

  async function respond(type) {
    setResponding(true);
    try {
      if (type === 'accept') await acceptCalendarEvent(event.id);
      else if (type === 'tentative') await tentativeCalendarEvent(event.id);
      else if (type === 'decline') await declineCalendarEvent(event.id);
      setResponse(type === 'accept' ? 'accepted' : type === 'tentative' ? 'tentativelyAccepted' : 'declined');
      onResponse?.();
    } catch {}
    setResponding(false);
  }

  const organiserEmail = event.organizer?.emailAddress?.address || '';
  const isOrganiser = event.responseStatus?.response === 'organizer';

  return (
    <div className="cal-event-detail">
      <div className="cal-event-detail-header">
        <h2 className="cal-event-title">{event.subject}</h2>
        <button className="cal-detail-close" onClick={onClose}>✕</button>
      </div>

      <div className="cal-event-meta">
        <div className="cal-meta-row">
          <span className="cal-meta-icon">🕐</span>
          <span>
            {isAllDay
              ? format(start, 'EEEE, d MMMM yyyy') + ' (All day)'
              : format(start, 'EEEE, d MMMM yyyy · h:mm a') + ' – ' + format(end, 'h:mm a')}
          </span>
        </div>
        {event.location?.displayName && (
          <div className="cal-meta-row">
            <span className="cal-meta-icon">📍</span>
            <span>{event.location.displayName}</span>
          </div>
        )}
        {organiserEmail && (
          <div className="cal-meta-row">
            <span className="cal-meta-icon">👤</span>
            <span>Organised by {event.organizer.emailAddress.name || organiserEmail}</span>
          </div>
        )}
        {event.attendees?.length > 0 && (
          <div className="cal-meta-row">
            <span className="cal-meta-icon">👥</span>
            <span>{event.attendees.map(a => a.emailAddress?.name || a.emailAddress?.address).join(', ')}</span>
          </div>
        )}
      </div>

      {!isOrganiser && (
        <div className="cal-response-bar">
          {response === 'accepted' && <span className="cal-response-confirmed accept">✓ Accepted</span>}
          {response === 'tentativelyAccepted' && <span className="cal-response-confirmed tentative">◑ Tentative</span>}
          {response === 'declined' && <span className="cal-response-confirmed decline">✕ Declined</span>}
          {response === 'none' || !response ? (
            <div className="cal-response-actions">
              <span className="cal-response-label">RSVP:</span>
              <button className="meeting-btn accept" onClick={() => respond('accept')} disabled={responding}>Accept</button>
              <button className="meeting-btn tentative" onClick={() => respond('tentative')} disabled={responding}>Tentative</button>
              <button className="meeting-btn decline" onClick={() => respond('decline')} disabled={responding}>Decline</button>
            </div>
          ) : (
            <div className="cal-response-actions">
              <span className="cal-response-label">Change:</span>
              {response !== 'accepted' && <button className="meeting-btn accept" onClick={() => respond('accept')} disabled={responding}>Accept</button>}
              {response !== 'tentativelyAccepted' && <button className="meeting-btn tentative" onClick={() => respond('tentative')} disabled={responding}>Tentative</button>}
              {response !== 'declined' && <button className="meeting-btn decline" onClick={() => respond('decline')} disabled={responding}>Decline</button>}
            </div>
          )}
        </div>
      )}

      {event.bodyPreview && (
        <div className="cal-event-body">{event.bodyPreview}</div>
      )}
    </div>
  );
}

function groupByDay(events) {
  const map = new Map();
  for (const ev of events) {
    const date = (ev.start.dateTime || ev.start.date).slice(0, 10);
    if (!map.has(date)) map.set(date, []);
    map.get(date).push(ev);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
}

function localDateTimeInput(d) {
  // Returns a datetime-local string in local time for an input default
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function NewEventForm({ onSave, onCancel }) {
  const now = new Date();
  const later = new Date(now.getTime() + 60 * 60 * 1000);
  const [subject, setSubject] = useState('');
  const [start, setStart] = useState(localDateTimeInput(now));
  const [end, setEnd] = useState(localDateTimeInput(later));
  const [location, setLocation] = useState('');
  const [body, setBody] = useState('');
  const [attendees, setAttendees] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!subject.trim()) { setError('Subject is required'); return; }
    setSaving(true);
    setError('');
    try {
      // Convert local datetime-local string to ISO (assume Sydney timezone server-side)
      await onSave({
        subject: subject.trim(),
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        location: location.trim(),
        body: body.trim(),
        attendees: attendees.split(',').map(s => s.trim()).filter(Boolean),
      });
    } catch (e) {
      setError('Failed to create event: ' + (e.response?.data?.detail?.error?.message || e.message));
      setSaving(false);
    }
  }

  return (
    <div className="cal-new-event-form">
      <div className="cal-event-detail-header">
        <h2 className="cal-event-title">New Event</h2>
        <button className="cal-detail-close" onClick={onCancel}>✕</button>
      </div>
      <form onSubmit={handleSubmit}>
        {error && <div className="cal-form-error">{error}</div>}
        <label className="cal-form-label">Subject *
          <input className="cal-form-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Meeting title" autoFocus />
        </label>
        <div className="cal-form-row">
          <label className="cal-form-label">Start
            <input className="cal-form-input" type="datetime-local" value={start} onChange={e => setStart(e.target.value)} />
          </label>
          <label className="cal-form-label">End
            <input className="cal-form-input" type="datetime-local" value={end} onChange={e => setEnd(e.target.value)} />
          </label>
        </div>
        <label className="cal-form-label">Location
          <input className="cal-form-input" value={location} onChange={e => setLocation(e.target.value)} placeholder="Room or address" />
        </label>
        <label className="cal-form-label">Attendees (comma-separated emails)
          <input className="cal-form-input" value={attendees} onChange={e => setAttendees(e.target.value)} placeholder="email@example.com, ..." />
        </label>
        <label className="cal-form-label">Notes
          <textarea className="cal-form-textarea" value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Optional description" />
        </label>
        <div className="cal-form-actions">
          <button type="submit" className="cal-save-btn" disabled={saving}>{saving ? 'Saving…' : 'Create Event'}</button>
          <button type="button" className="cal-cancel-btn" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default function CalendarView() {
  const [month, setMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);

  useEffect(() => {
    loadEvents();
  }, [month]);

  async function loadEvents() {
    setLoading(true);
    setError('');
    try {
      const start = startOfMonth(month).toISOString();
      const end = endOfMonth(month).toISOString();
      const data = await getCalendarEvents(start, end);
      setEvents(data);
    } catch (e) {
      setError('Failed to load calendar: ' + e.message);
    }
    setLoading(false);
  }

  const grouped = groupByDay(events);

  return (
    <div className="calendar-view">
      <div className="cal-header">
        <button className="cal-nav-btn" onClick={() => setMonth(m => subMonths(m, 1))}>‹</button>
        <h2 className="cal-month-title">{format(month, 'MMMM yyyy')}</h2>
        <button className="cal-nav-btn" onClick={() => setMonth(m => addMonths(m, 1))}>›</button>
        <button className="cal-today-btn" onClick={() => setMonth(new Date())}>Today</button>
        <button className="cal-refresh-btn" onClick={loadEvents} title="Refresh">↻</button>
        <button className="cal-new-btn" onClick={() => { setShowNewForm(true); setSelectedEvent(null); }}>＋ New Event</button>
      </div>

      {error && <div className="cal-error">{error}</div>}

      <div className="cal-body">
        {loading ? (
          <div className="cal-loading"><div className="loading-spinner" /></div>
        ) : grouped.length === 0 ? (
          <div className="cal-empty">No events this month</div>
        ) : (
          <div className="cal-agenda">
            {grouped.map(([date, dayEvents]) => {
              const d = parseISO(date);
              const today = isToday(d);
              return (
                <div key={date} className="cal-day-group">
                  <div className={`cal-day-label ${today ? 'today' : ''}`}>
                    <span className="cal-day-name">{format(d, 'EEE')}</span>
                    <span className={`cal-day-num ${today ? 'today' : ''}`}>{format(d, 'd')}</span>
                    <span className="cal-month-name">{format(d, 'MMM')}</span>
                  </div>
                  <div className="cal-day-events">
                    {dayEvents.map(ev => {
                      const start = parseISO(ev.start.dateTime || ev.start.date);
                      const color = SHOW_AS_COLORS[ev.showAs] || '#0078d4';
                      const resp = ev.responseStatus?.response;
                      return (
                        <div
                          key={ev.id}
                          className={`cal-event-chip ${selectedEvent?.id === ev.id ? 'selected' : ''}`}
                          onClick={() => setSelectedEvent(selectedEvent?.id === ev.id ? null : ev)}
                          style={{ borderLeftColor: color }}
                        >
                          <div className="cal-event-chip-top">
                            <span className="cal-event-chip-time">
                              {ev.isAllDay ? 'All day' : format(start, 'h:mm a')}
                            </span>
                            {resp && resp !== 'none' && resp !== 'organizer' && (
                              <span className={`cal-rsvp-badge ${resp}`}>
                                {resp === 'accepted' ? '✓' : resp === 'tentativelyAccepted' ? '◑' : '✕'}
                              </span>
                            )}
                          </div>
                          <div className="cal-event-chip-subject">{ev.subject}</div>
                          {ev.location?.displayName && (
                            <div className="cal-event-chip-location">📍 {ev.location.displayName}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedEvent && !showNewForm && (
          <EventDetail
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onResponse={loadEvents}
          />
        )}
        {showNewForm && (
          <NewEventForm
            onSave={async (payload) => {
              await createCalendarEvent(payload);
              setShowNewForm(false);
              loadEvents();
            }}
            onCancel={() => setShowNewForm(false)}
          />
        )}
      </div>
    </div>
  );
}
